import { Link } from "react-router-dom";
import SiteShell from "@/components/feature/SiteShell";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SubscribeForm from "@/components/feature/SubscribeForm";
import useSeo from "@/hooks/useSeo";
import { siteStats } from "@/mocks/timeline";

const principles = [
  {
    icon: "ri-file-list-3-line",
    title: "先有出处，再下结论",
    text: "每条旧料都尽量标清大致的年份和可查证的来源。查不到出处的说法，我们会写明「未获证实」，而不是当成事实写进去。",
  },
  {
    icon: "ri-shield-user-line",
    title: "涉及隐私的，一律匿名",
    text: "未经证实的说法涉及具体个人时，只保留事件本身。考古是让事情被看清，不是让某个人再被推上一次风口。",
  },
  {
    icon: "ri-delete-bin-6-line",
    title: "该更正就更正，该撤就撤",
    text: "如果文中信息有误、涉及隐私，收到说明并核实后，我们会补充更正或者直接撤下条目，不拖。",
  },
  {
    icon: "ri-emotion-normal-line",
    title: "不追新，不煽动",
    text: "我们不做标题党，也不把旧事重新炒热。想做的只是把年份理清楚，把故事讲完整。",
  },
];

const AboutPage = () => {
  useSeo({
    title: "关于我们 - 考古坞娱乐往事档案室",
    description:
      "考古坞是一个做热门黑料历史回顾与旧料翻出的娱乐往事档案室。了解我们的网站定位、内容原则、整理方法与不确定信息的标注方式。",
    keywords: "关于考古坞,娱乐往事档案室,旧料翻出原则,考古合集定位",
    image:
      "https://readdy.ai/api/search-image?query=rows%20of%20labeled%20archive%20boxes%20and%20old%20photo%20albums%20on%20wooden%20shelves%2C%20warm%20afternoon%20light%20through%20a%20dusty%20window%2C%20quiet%20editorial%20mood%2C%20deep%20brick%20red%20and%20aged%20gold%20tones%2C%20artistic%20stylized%2C%20no%20people&width=1000&height=750&seq=arch-about-2026&orientation=landscape",
  });

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于考古坞",
      inLanguage: "zh-CN",
      url: "https://kaogudock.cn/about",
      description: "考古坞的网站定位、内容原则与娱乐往事整理方法。",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "考古坞",
      alternateName: "娱乐往事档案室",
      url: "https://kaogudock.cn",
      email: "hello@kaogudock.cn",
      description:
        "考古坞专注热门黑料历史回顾与旧料翻出，把娱乐行业里被时间盖住的旧事按年代归档整理。",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://kaogudock.cn/" },
        { "@type": "ListItem", position: 2, name: "关于我们", item: "https://kaogudock.cn/about" },
      ],
    },
  ];

  return (
    <SiteShell>
      <JsonLd data={structuredData} id="about-jsonld" />

      <section className="relative overflow-hidden border-b border-foreground-950/10">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=rows%20of%20labeled%20archive%20boxes%20and%20old%20photo%20albums%20on%20wooden%20shelves%2C%20warm%20afternoon%20light%20through%20a%20dusty%20window%2C%20quiet%20editorial%20mood%2C%20deep%20brick%20red%20and%20aged%20gold%20tones%2C%20artistic%20stylized%2C%20no%20people&width=1000&height=750&seq=arch-about-2026&orientation=landscape"
            alt="整齐码放着标注盒与旧相册的档案架"
            title="考古坞 · 娱乐往事档案室"
            className="h-full w-full object-cover object-top"
          />
          <span className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/75 to-foreground-950/45"></span>
        </div>
        <div className="relative mx-auto max-w-shell px-4 py-14 md:px-6 md:py-20">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <p className="mt-5 font-label text-xs tracking-[0.3em] text-accent-400">
            ABOUT · 娱乐往事档案室
          </p>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-black leading-tight text-background-50 md:text-4xl">
            我们把旧料当成档案，而不是谈资
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            考古坞成立于 2026 年，专注做娱乐往事整理。这里没有即时热搜，只有一条一条被重新编号、
            重新核对过的旧条目。我们相信，时间拉长之后，值得被看见的是事情本身，而不是当时的声音有多大。
          </p>
        </div>
      </section>

      <section className="bg-background-50 paper-grain">
        <div className="mx-auto max-w-shell px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {siteStats.map((stat) => (
              <div key={stat.label} className="rounded-lg border border-foreground-950/10 bg-background-100 p-5">
                <p className="font-heading text-3xl font-black text-primary-600">{stat.value}</p>
                <p className="mt-1 font-label text-xs text-foreground-600">
                  {stat.label} / {stat.unit}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto max-w-shell px-4 py-14 md:px-6 md:py-20">
          <div className="border-t-2 border-foreground-950/80 pt-5">
            <p className="font-label text-xs tracking-[0.28em] text-primary-600">内容原则</p>
            <h2 className="mt-2 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
              四条我们不会让步的规矩
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <article
                key={item.title}
                className="rounded-lg border border-foreground-950/10 bg-background-50 p-5 md:p-6"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className={`${item.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-shell px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="font-label text-xs tracking-[0.28em] text-primary-600">怎么整理</p>
              <h2 className="mt-2 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
                一条旧料要过三道手
              </h2>
              <ol className="mt-6 space-y-5">
                {[
                  { step: "收集", text: "先从公开录像、当年报刊、论坛存档和当事人多年后的采访里，把材料凑齐。" },
                  { step: "对照", text: "同一件事至少找两个独立来源交叉比对，时间线对不上的地方单独标出来。" },
                  { step: "归档", text: "写清年份、主题和出处，再放进对应的年代区间；存疑的部分保留「未获证实」标注。" },
                ].map((item, index) => (
                  <li key={item.step} className="flex gap-4">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-500 font-label text-sm text-background-50">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-heading text-base font-bold text-foreground-950">{item.step}</p>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <Link
                to="/archive"
                className="mt-8 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-archive-line text-base"></i>
                去看看整理好的合集
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-lg border border-foreground-950/10 bg-background-100 p-5 md:p-7">
                <p className="font-label text-xs tracking-[0.28em] text-primary-600">联系我们</p>
                <h2 className="mt-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                  投稿、更正或只是想聊聊
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  不管是线索、更正请求，还是关于某一年的一句补充，都可以通过下面的表单交给我们。
                  邮箱：hello@kaogudock.cn
                </p>
                <div className="mt-5">
                  <SubscribeForm />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
};

export default AboutPage;